import { memo, useContext } from "react";
import { useApolloClient } from "@apollo/client";
import { addMonths, format } from "date-fns";
import { AppConfigContext, buildPath, convertFromServerTime, loadData, MimicExcelColumn, useExportToExcel, useSnackbar } from "@tecorien/mimic";
import {
  DriverProfileDto,
  FilterExpressionOperatorEnum,
  FilterOperatorEnum,
  GetDriverProfilesForLopDocument,
  IncludedInLopStatus,
} from "generated/graphql";
import { getFleetLabel } from "utils/formatter";
import additionalData from "./additonalData.json";
import { AdditionalInfoType } from "./types";

export const LOPReport = memo(() => {
  const apolloClient = useApolloClient();
  const { push } = useSnackbar();
  const appConfig = useContext(AppConfigContext);
  const monthName = format(addMonths(new Date(), -1), "MMMM");
  const columns: (MimicExcelColumn<DriverProfileDto> & { additionalHeader?: string })[] = [
    {
      name: "Fleet",
      cellAccessor: data => getFleetLabel(data.fleet),
      width: 20,
      filterButton: true,
    },
    {
      name: "Employee First Name",
      cellAccessor: data => data.firstName,
      width: 20,
      filterButton: true,
    },
    {
      name: "Employee Last Name",
      cellAccessor: data => data.lastName,
      width: 20,
      filterButton: true,
    },
    {
      name: "Job title",
      additionalHeader: "provide the job title of the employee. examples: home health aide, physical therapist, etc.",
      cellAccessor: () => "Driver",
      width: 24,
      filterButton: true,
    },
    {
      name: "Date of hire",
      additionalHeader: "date the employee was hired by the po. submit in mm/dd/yyyy format (e.g., 01/01/2020).",
      cellAccessor: data => data.startDate ? convertFromServerTime(
        data.startDate,
        "UTC",
        appConfig.serverDateTimeFormatMoment,
      )
        .toDate() : null,
      width: 15,
      numFmt: "mm/dd/yyyy",
      filterButton: true,
    },
    {
      name: "Date of Termination",
      additionalHeader: "Date the employee was terminated or resigned from the PO. Submit in MM/DD/YYYY format (e.g., 01/01/2020).  Enter NA if not applicable.",
      cellAccessor: data => data.terminationDate ? convertFromServerTime(
        data.terminationDate,
        "UTC",
        appConfig.serverDateTimeFormatMoment,
      )
        .toDate() : "NA",
      numFmt: "mm/dd/yyyy",
      width: 21,
      filterButton: true,
    },
    {
      name: "Type of Employment",
      additionalHeader: "Provide the type of employment for the employee. Valid entries are: contract, Full-time, Part-time, Volunteer, or Other.",
      cellAccessor: () => "Full-time contract",
      width: 16,
      filterButton: true,
    },
    {
      name: "Direct Participant Care",
      additionalHeader: "Enter Y if the employee had direct participant contact during the data collection period.\r\nEnter N if the employee did not have direct participant contact during the data collection period.",
      cellAccessor: () => "Y",
      width: 16,
      filterButton: true,
    },
    {
      name: "License",
      additionalHeader: "Enter Y if the employee requires a license in order to perform their duties\r\nEnter N if the employee does not require a license in order to perform their duties",
      cellAccessor: () => "Y",
      width: 16,
      filterButton: true,
    },
    {
      name: "IDT Member",
      additionalHeader: "Enter Y if the employee is a part of the Pace IDT\r\nEnter N if the employee is not a member of the Pace Organizations IDT.",
      cellAccessor: () => "Y",
      width: 16,
      filterButton: true,
    },
    {
      name: "IDT Role",
      additionalHeader: "PCP, RN, MSW, Home Care Coordinator, OT, PT, Dietitian, Recreational Therapist/Activities Coordinator, Personal Care Attendant, Transportation, Center Manager, Other.\r\nEnter NA if the individual is not a part of an IDT.",
      cellAccessor: () => "Transportation",
      width: 45,
      filterButton: true,
    },
  ];

  const exportToExcel = useExportToExcel({
    fileName: `Centerlight Driver Master List ${monthName}`,
    getItems: async () => {
      const driverProfileDtos = await loadData<DriverProfileDto>(GetDriverProfilesForLopDocument, {
        client: apolloClient as any,
        filter: {
          expr: {
            operator: FilterExpressionOperatorEnum.And,
            children: [
              {
                entry: {
                  path: buildPath<DriverProfileDto>()("startDate")
                    .toString(),
                  operator: FilterOperatorEnum.NotNull,
                },
              },
              {
                entry: {
                  path: buildPath<DriverProfileDto>()("includedInLOP")
                    .toString(),
                  operator: FilterOperatorEnum.Equal,
                  value: IncludedInLopStatus.Included,
                },
              },
            ],
          },
        },
        onProgress: (e => {
          push({
            key: "LOPFile",
            type: "info",
            message: `Processed ${e.loadedItems}/${e.totalItems}`,
          });
        }),
        collectItems: true,
      });
      return [
        ...Array.from((additionalData as any as AdditionalInfoType[])
          .filter(x => x.type !== "e")
          .reduce((map, v) => {
            const key = `${v.type}_${v.first_name}_${v.last_name}_${v.start_date}`;
            map.set(key, v);
            return map;
          }, new Map<string, AdditionalInfoType>())
          .values())
          .map(x => ({
            firstName: x.first_name,
            lastName: x.last_name,
            startDate: x.start_date,
            terminationDate: x.termination_date,
          } as DriverProfileDto)),
        ...driverProfileDtos,
      ];
    },
    columns,
    additionalProps: {
      modify: (workbook, worksheet) => {
        workbook.properties.date1904 = false;
        worksheet.removeTable("Table");
        const headerRow = worksheet.getRow(1);
        headerRow.height = 23;
        headerRow
          .eachCell((cell, colNumber) => {
            cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            cell.value = {
              richText: [ {
                text: cell.value!.toString(),
                font: { bold: true },
              }, ...(columns[colNumber - 1]?.additionalHeader ? [ {
                text: "\r\n" + columns[colNumber - 1].additionalHeader!,
              } ] : []) ],
            };
          });
      },
    },
  });
  return (
    <button
      style={{ all: "inherit", display: "contents" }}
      onClick={() => exportToExcel()}
    >
      Download LOP list
    </button>
  );
});
